import React, { useRef, useState } from "react";
import { Button, Input, Segmented, Space, Tooltip } from "antd";
import {
  BoldOutlined,
  ItalicOutlined,
  LinkOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { connect, mapReadPretty } from "@formily/react";
import { useTranslation } from "react-i18next";
import MarkdownView from "./markdown/MarkdownView";
import { MAX_LENGTH } from "./markdown/markdown";

/**
 * A formatted answer -- a method statement, a scope of work, a set of findings
 * (C12).
 *
 * **A toolbar over a plain textarea, not a WYSIWYG editor**, and the reasoning
 * is in `main/richtext.py`: the stored value is markdown, which keeps the answer
 * readable in a report, exportable to a spreadsheet, printable as real Word
 * paragraphs, and free of any markup that would need sanitising. What the
 * toolbar adds is the thing people actually miss from a plain textarea -- they
 * do not want to *type* `**`, they want to select a phrase and press a button.
 *
 * Two decisions worth keeping:
 *
 * * **Edit and Preview are a toggle, not a split pane.** On a phone (the field
 *   app renders the same component) a side-by-side preview halves an already
 *   small typing area, and a permit's description is written in one go and read
 *   afterwards rather than watched as it is typed.
 * * **The selection is preserved after a toolbar press.** Losing it means the
 *   second button somebody presses applies to nothing, which reads as the
 *   toolbar being broken.
 */

type Wrap = { before: string; after: string };
type LinePrefix = { prefix: string; numbered?: boolean };

const RichTextEditor = ({ value, onChange, disabled, placeholder, rows = 6 }: any) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<"write" | "preview">("write");
  const areaRef = useRef<any>(null);
  const text: string = value ?? "";

  const area = (): HTMLTextAreaElement | null =>
    areaRef.current?.resizableTextArea?.textArea ?? null;

  const apply = (next: string, selection: [number, number]) => {
    onChange?.(next);
    // After the value round-trips through the form store the DOM selection is
    // gone; restoring it on the next frame is what makes two consecutive
    // toolbar presses work on the same phrase.
    requestAnimationFrame(() => {
      const el = area();
      if (!el) return;
      el.focus();
      el.setSelectionRange(selection[0], selection[1]);
    });
  };

  const wrap = ({ before, after }: Wrap) => {
    const el = area();
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    const selected = text.slice(start, end) || t("text");
    const next = text.slice(0, start) + before + selected + after + text.slice(end);
    apply(next, [start + before.length, start + before.length + selected.length]);
  };

  const prefixLines = ({ prefix, numbered }: LinePrefix) => {
    const el = area();
    const start = el?.selectionStart ?? 0;
    const end = el?.selectionEnd ?? 0;
    // Grow the selection to whole lines: half a line cannot become a bullet.
    const from = text.lastIndexOf("\n", Math.max(0, start - 1)) + 1;
    const toIndex = text.indexOf("\n", end);
    const to = toIndex === -1 ? text.length : toIndex;
    const lines = text.slice(from, to).split("\n");
    const marked = lines
      .map((line, i) => (numbered ? `${i + 1}. ${line}` : `${prefix}${line}`))
      .join("\n");
    apply(text.slice(0, from) + marked + text.slice(to), [from, from + marked.length]);
  };

  const tools = [
    { key: "bold", icon: <BoldOutlined />, label: t("Bold"), run: () => wrap({ before: "**", after: "**" }) },
    { key: "italic", icon: <ItalicOutlined />, label: t("Italic"), run: () => wrap({ before: "*", after: "*" }) },
    { key: "bullet", icon: <UnorderedListOutlined />, label: t("Bulleted list"), run: () => prefixLines({ prefix: "- " }) },
    { key: "number", icon: <OrderedListOutlined />, label: t("Numbered list"), run: () => prefixLines({ prefix: "", numbered: true }) },
    { key: "link", icon: <LinkOutlined />, label: t("Link"), run: () => wrap({ before: "[", after: "](https://)" }) },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          marginBottom: 6,
          flexWrap: "wrap",
        }}
      >
        <Space size={2}>
          {tools.map((tool) => (
            <Tooltip key={tool.key} title={tool.label}>
              <Button
                size="small"
                type="text"
                icon={tool.icon}
                disabled={disabled || mode === "preview"}
                aria-label={tool.label}
                // `onMouseDown` rather than `onClick`: a click has already
                // blurred the textarea by the time it fires, taking the
                // selection the button is meant to act on with it.
                onMouseDown={(event) => {
                  event.preventDefault();
                  tool.run();
                }}
              />
            </Tooltip>
          ))}
        </Space>
        <Segmented
          size="small"
          value={mode}
          onChange={(next) => setMode(next as "write" | "preview")}
          options={[
            { label: t("Write"), value: "write" },
            { label: t("Preview"), value: "preview" },
          ]}
        />
      </div>

      {mode === "write" ? (
        <Input.TextArea
          ref={areaRef}
          value={text}
          disabled={disabled}
          placeholder={placeholder}
          autoSize={{ minRows: rows, maxRows: 24 }}
          maxLength={MAX_LENGTH}
          onChange={(event) => onChange?.(event.target.value)}
        />
      ) : (
        <div
          style={{
            border: "1px solid #d9d9d9",
            borderRadius: 6,
            padding: "8px 11px",
            minHeight: rows * 22,
            background: "#fff",
          }}
        >
          {text.trim() ? (
            <MarkdownView value={text} />
          ) : (
            <span style={{ color: "#8c8c8c" }}>{t("Nothing written yet")}</span>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * `mapReadPretty` gives a submitted permit the rendered answer rather than the
 * markdown source. Without it Formily substitutes `PreviewText`, which would
 * print the asterisks on a closed permit -- the one place the formatting has to
 * be right, because that is what people read.
 */
const RichTextInput = connect(
  RichTextEditor,
  mapReadPretty(({ value }: any) =>
    value ? <MarkdownView value={value} /> : <span style={{ color: "#8c8c8c" }}>—</span>
  )
);

export default RichTextInput;
